export function toDoseMg(value, unit) {
  if (unit === 'mg') return value;
  if (unit === 'mcg') return value * 0.001;
  throw new Error(`Unknown dose unit: ${unit}`);
}

export function computeConcentrationMgPerMl(vialMg, bacMl) {
  return vialMg / bacMl;
}

export function computeVolumeMl(doseMg, concentrationMgPerMl) {
  return doseMg / concentrationMgPerMl;
}

export function computeDoseMg(volumeMl, concentrationMgPerMl) {
  return volumeMl * concentrationMgPerMl;
}

export function computeUnits(volumeMl) {
  return volumeMl * 100;
}

export function computeVolumeMlFromUnits(units) {
  return units / 100;
}

export const LIMITS = {
  vial_mg:       { min: 0.1,   max: 5000 },
  bac_ml:        { min: 0.5,   max: 10 },
  dose_mg:       { min: 0.001, max: 200 },
  dose_mcg:      { min: 1,     max: 200000 },
  injected_ml:   { min: 0.001, max: 10 },
  injected_units:{ min: 0.1,   max: 1000 },
};

export function validateField(name, value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return { valid: false, error: 'Enter a number' };
  }
  if (value <= 0) {
    return { valid: false, error: 'Must be greater than 0' };
  }
  const limit = LIMITS[name];
  if (!limit) return { valid: true };
  if (value < limit.min || value > limit.max) {
    return { valid: false, error: `Must be between ${limit.min} and ${limit.max}` };
  }
  return { valid: true };
}

export const SYRINGE_CAPACITY_ML = {
  '0.3ml-u100':     0.3,
  '0.5ml-u100':     0.5,
  '1ml-u100':       1.0,
};

export const IS_U100 = {
  '0.3ml-u100':     true,
  '0.5ml-u100':     true,
  '1ml-u100':       true,
};

export function calculate({ vialMg, bacMl, doseValue, doseUnit, syringeType }) {
  const doseMg = toDoseMg(doseValue, doseUnit);
  const concentrationMgPerMl = computeConcentrationMgPerMl(vialMg, bacMl);
  const volumeMl = computeVolumeMl(doseMg, concentrationMgPerMl);
  const capacityMl = SYRINGE_CAPACITY_ML[syringeType];

  if (volumeMl > capacityMl) {
    return {
      overflow: true,
      volumeMl,
      capacityMl,
      syringeType,
    };
  }

  const showsUnits = IS_U100[syringeType];

  return {
    overflow: false,
    concentrationMgPerMl,
    volumeMl,
    units: showsUnits ? computeUnits(volumeMl) : null,
    showsUnits,
  };
}

export function calculateDoseFromInjectedVolume({ vialMg, bacMl, injectedValue, injectedUnit, syringeType }) {
  const concentrationMgPerMl = computeConcentrationMgPerMl(vialMg, bacMl);
  const volumeMl = injectedUnit === 'units'
    ? computeVolumeMlFromUnits(injectedValue)
    : injectedValue;
  const capacityMl = SYRINGE_CAPACITY_ML[syringeType];

  if (volumeMl > capacityMl) {
    return {
      overflow: true,
      volumeMl,
      capacityMl,
      syringeType,
      injectedUnit,
    };
  }

  const doseMg = computeDoseMg(volumeMl, concentrationMgPerMl);
  const showsUnits = IS_U100[syringeType];

  return {
    overflow: false,
    concentrationMgPerMl,
    volumeMl,
    units: showsUnits ? computeUnits(volumeMl) : null,
    doseMg,
    doseMcg: doseMg * 1000,
    showsUnits,
  };
}

const SYRINGE_LABELS = {
  '0.3ml-u100':     '0.3 ml U-100',
  '0.5ml-u100':     '0.5 ml U-100',
  '1ml-u100':       '1 ml U-100',
};

export function formatOutput(result) {
  if (result.overflow) {
    const label = SYRINGE_LABELS[result.syringeType] || result.syringeType;
    const subject = result.injectedUnit ? 'Injected amount' : 'Dose volume';
    return {
      type: 'overflow',
      message: `${subject} (${result.volumeMl.toFixed(3)} ml) exceeds the capacity of a ${label} syringe. Check your inputs or choose a larger syringe.`,
    };
  }
  return {
    type: 'ok',
    concentration: `${result.concentrationMgPerMl.toFixed(2)} mg/ml`,
    volume: `${result.volumeMl.toFixed(3)} ml`,
    units: result.showsUnits ? result.units.toFixed(1) : null,
  };
}

export const STORAGE_KEY = 'is_dose_calc_last';
export const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

export function saveLastCalc(inputs, storage, nowMs) {
  const payload = {
    mode: inputs.mode || 'target-dose',
    vialMg: inputs.vialMg,
    bacMl: inputs.bacMl,
    doseValue: inputs.doseValue,
    doseUnit: inputs.doseUnit,
    injectedValue: inputs.injectedValue,
    injectedUnit: inputs.injectedUnit,
    syringeType: inputs.syringeType,
    timestampMs: nowMs,
  };
  storage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function loadLastCalc(storage, nowMs) {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed.timestampMs !== 'number') return null;
    if (nowMs - parsed.timestampMs > EXPIRY_MS) return null;
    // Every input field must be present and meaningful, otherwise the banner
    // would offer to restore an incomplete or zeroed calculation.
    if (!isFinite(parsed.vialMg) || parsed.vialMg <= 0) return null;
    if (!isFinite(parsed.bacMl) || parsed.bacMl <= 0) return null;
    const mode = parsed.mode === 'injected-volume' ? 'injected-volume' : 'target-dose';
    if (mode === 'target-dose') {
      if (!isFinite(parsed.doseValue) || parsed.doseValue <= 0) return null;
      if (parsed.doseUnit !== 'mg' && parsed.doseUnit !== 'mcg') return null;
    } else {
      if (!isFinite(parsed.injectedValue) || parsed.injectedValue <= 0) return null;
      if (parsed.injectedUnit !== 'ml' && parsed.injectedUnit !== 'units') return null;
    }
    if (!parsed.syringeType) return null;
    return { ...parsed, mode };
  } catch {
    return null;
  }
}

export function clearLastCalc(storage) {
  storage.removeItem(STORAGE_KEY);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootstrap());
  } else {
    bootstrap();
  }
}

function bootstrap() {
  const root = document.querySelector('.is-calc');
  if (!root) return;
  initDoseCalculator(root);
}

function initDoseCalculator(root) {
  const $  = (sel) => root.querySelector(sel);
  const $$ = (sel) => root.querySelectorAll(sel);

  const state = {
    mode: 'target-dose',
    syringe: null,
    vial: null,
    bac: null,
    doseUnit: 'mcg',
    dose: null,
    injectedUnit: 'units',
    injected: null,
  };

  root.dataset.mode = state.mode;

  const resultEmpty     = $('#is-calc-result-empty');
  const resultOk        = $('#is-calc-result-ok');
  const resultOverflow  = $('#is-calc-result-overflow');
  const headlineVerb    = $('#is-calc-headline-verb');
  const headlineValue   = $('#is-calc-headline-value');
  const headlineUnit    = $('#is-calc-headline-unit');
  const sublineEl       = $('#is-calc-subline');
  const lifetimeEl      = $('#is-calc-lifetime');
  const overflowMsg     = $('#is-calc-overflow-message');
  const restoreChip     = $('#is-calc-restore-chip');
  const restoreSummary  = $('#is-calc-restore-summary');
  const restoreYes      = $('#is-calc-restore-yes');
  const restoreNo       = $('#is-calc-restore-no');
  const resetBtn        = $('#is-calc-reset');
  const printBtn        = $('#is-calc-print');
  const planCycleLink   = $('#is-calc-plan-cycle');
  const modeToggles     = $$('.is-calc__mode-toggle');
  const step4Title      = $('#is-calc-step4-title');
  const step4Hint       = $('#is-calc-step4-hint');
  const dosePanel       = $('.is-calc__dose-panel');
  const injectedPanel   = $('.is-calc__injected-panel');
  // mini-syringe (step 1) - H-style anatomical SVG
  const miniSyringeBox  = $('#is-calc-mini-syringe');
  const miniSvg         = $('#is-calc-mini-svg');
  const miniBarrel      = $('#is-calc-mini-barrel');
  const miniFluid       = $('#is-calc-mini-fluid');
  const miniSeal        = $('#is-calc-mini-seal');
  const miniPlate       = $('#is-calc-mini-plate');
  const miniRod         = $('#is-calc-mini-rod');
  const miniThumb       = $('#is-calc-mini-thumb');
  const miniTicks       = $('#is-calc-mini-ticks');
  const miniNums        = $('#is-calc-mini-nums');
  const miniCaption     = $('#is-calc-mini-caption');
  // result-block syringe (after step 4) - same anatomy, larger
  const syringeBox      = $('#is-calc-syringe');
  const syringeSvg      = $('#is-calc-syringe-svg');
  const syringeBarrel   = $('#is-calc-syringe-barrel');
  const syringeFluid    = $('#is-calc-syringe-fluid');
  const syringeSeal     = $('#is-calc-syringe-seal');
  const syringePlate    = $('#is-calc-syringe-plate');
  const syringeRod      = $('#is-calc-syringe-rod');
  const syringeThumb    = $('#is-calc-syringe-thumb');
  const syringeTicks    = $('#is-calc-syringe-ticks');
  const syringeNums     = $('#is-calc-syringe-nums');
  const syringeCaption  = $('#is-calc-syringe-caption');

  const pills       = $$('.is-calc__pill');
  const otherBlocks = $$('.is-calc__other-input');
  const unitToggles = $$('.is-calc__dose-panel .is-calc__unit-toggle');
  const injectedUnitToggles = $$('.is-calc__injected-unit-toggle');

  function clearPillsInGroup(field) {
    $$(`.is-calc__pill[data-field="${field}"]`).forEach(p => {
      p.classList.remove('is-calc__pill--active');
      p.setAttribute('aria-checked', 'false');
    });
  }

  function selectPill(pill) {
    pill.classList.add('is-calc__pill--active');
    pill.setAttribute('aria-checked', 'true');
  }

  function getOtherBlock(field) {
    return root.querySelector(`.is-calc__other-input[data-field="${field}"]`);
  }

  function showErr(el, msg) { if (el) { el.textContent = msg; el.hidden = false; } }
  function hideErr(el)      { if (el) { el.textContent = '';  el.hidden = true;  } }

  function hideResult() {
    resultEmpty.hidden = false;
    resultOk.hidden = true;
    resultOverflow.hidden = true;
    const rt = document.getElementById('is-calc-round-tip');
    if (rt) rt.hidden = true;
    if (lifetimeEl) lifetimeEl.hidden = true;
    if (syringeBox) syringeBox.hidden = true;
    if (window.announceCalcResult) window.announceCalcResult(null);
  }

  // Approved-mock anatomy (2026-04-29): syringe physical dimensions are FIXED;
  // only the calibration (tick density + numbers) and the plunger seal position change with capacity.
  // viewBox 0 0 800 90; barrel x=80..680 (fixed 600 wide), y=22..55 (height 33).
  // Numbers OUTSIDE the barrel below it (y=71) so they're in front of the plunger visually.
  // Plunger has flat-end plate behind the rubber seal; thumb plate sits past the rod end at x=740.
  const SX_BARREL_X = 80;
  const SX_BARREL_END = 680;
  const SX_BARREL_W = 600;
  const SX_BARREL_TOP = 22;
  const SX_BARREL_BOTTOM = 55;
  const SX_SEAL_W = 14;
  const SX_PLATE_W = 6;
  const SX_ROD_END = 740;

  function paintSyringe(refs, capacity, drawnMl) {
    if (!refs.barrel) return;
    const drawnFrac = Math.max(0, Math.min(1, drawnMl / capacity));
    const sealX = SX_BARREL_X + drawnFrac * SX_BARREL_W;
    // Fluid: from front of barrel up to the seal
    if (refs.fluid) refs.fluid.setAttribute('width', (sealX - SX_BARREL_X).toFixed(2));
    // Seal (orange rubber stopper) at draw position
    refs.seal.setAttribute('x', sealX.toFixed(2));
    // Plunger plate (flat end of plunger - the plastic disk pushing the seal)
    if (refs.plate) refs.plate.setAttribute('x', (sealX + SX_SEAL_W).toFixed(2));
    // Rod from behind plunger plate to thumb
    const rodStart = sealX + SX_SEAL_W + SX_PLATE_W;
    refs.rod.setAttribute('x', rodStart.toFixed(2));
    refs.rod.setAttribute('width', (SX_ROD_END - rodStart).toFixed(2));
    // Calibration: tick density + label frequency adapt to capacity (only thing that changes)
    const totalUnits = capacity * 100;
    const tickEvery = totalUnits <= 50 ? 1 : 2;
    const labelEvery = totalUnits <= 30 ? 5 : (totalUnits <= 50 ? 5 : 10);
    const tItems = [];
    const nItems = [];
    for (let u = 0; u <= totalUnits + 1e-6; u += tickEvery) {
      const x = SX_BARREL_X + (u / totalUnits) * SX_BARREL_W;
      const isLabel = Math.abs((u / labelEvery) - Math.round(u / labelEvery)) < 1e-6;
      const tickH = isLabel ? 7 : 3.5;
      tItems.push(`<line class="is-calc__sx-tick" x1="${x.toFixed(2)}" y1="${SX_BARREL_TOP}" x2="${x.toFixed(2)}" y2="${(SX_BARREL_TOP + tickH).toFixed(2)}" stroke-width="${isLabel ? 1.2 : 0.6}"/>`);
      if (u > 0 && isLabel) {
        nItems.push(`<text class="is-calc__sx-num" x="${x.toFixed(2)}" y="71" font-size="11">${Math.round(u)}</text>`);
      }
    }
    refs.ticks.innerHTML = tItems.join('');
    refs.nums.innerHTML = nItems.join('');
  }

  // Mini-syringe: shown next to step 1 pills, scales to selected capacity.
  // For step 1 we show a 50% drawn preview so the user sees "this is how it'll look filled" (Option H look).
  function renderMiniSyringe(syringeKey) {
    if (!miniBarrel) return;
    const capacity = SYRINGE_CAPACITY_ML[syringeKey];
    if (!capacity) return;
    // Draw at 50% to demo the syringe in use; updateMiniFill() can override with real volume later
    paintSyringe({
      svg: miniSvg, barrel: miniBarrel, fluid: miniFluid, seal: miniSeal, plate: miniPlate,
      rod: miniRod, thumb: miniThumb, ticks: miniTicks, nums: miniNums
    }, capacity, capacity * 0.5);
    if (miniCaption) {
      miniCaption.textContent = `${Math.round(capacity*100)} units (${capacity} mL) U-100 insulin syringe`;
    }
  }

  // Once the user has filled the form, the mini-syringe shows the actual draw position
  function updateMiniFill(volumeMl, capacityMl) {
    if (!miniBarrel || !capacityMl || !isFinite(volumeMl)) return;
    paintSyringe({
      svg: miniSvg, barrel: miniBarrel, fluid: miniFluid, seal: miniSeal, plate: miniPlate,
      rod: miniRod, thumb: miniThumb, ticks: miniTicks, nums: miniNums
    }, capacityMl, volumeMl);
  }

  // Result-block syringe: same anatomy, scaled bigger, draws to actual calculated volume
  function renderSyringe(result) {
    if (!syringeBox || !syringeBarrel) return;
    const capacity = result.capacityMl != null ? result.capacityMl : SYRINGE_CAPACITY_ML[state.syringe];
    if (!capacity || !isFinite(result.volumeMl)) { syringeBox.hidden = true; return; }
    paintSyringe({
      svg: syringeSvg, barrel: syringeBarrel, fluid: syringeFluid, seal: syringeSeal, plate: syringePlate,
      rod: syringeRod, thumb: syringeThumb, ticks: syringeTicks, nums: syringeNums
    }, capacity, result.volumeMl);
    if (syringeCaption) {
      const drawnUnits = result.units != null ? (result.units.toFixed(result.units < 10 ? 1 : 0) + ' units') : (result.volumeMl.toFixed(2) + ' mL');
      const capLabel = Math.round(capacity * 100) + ' units (' + capacity + ' mL)';
      syringeCaption.textContent = 'Pull plunger to ' + drawnUnits + ' on a ' + capLabel + ' syringe.';
    }
    syringeBox.hidden = false;
  }

  function formatHeadline(result) {
    if (state.mode === 'injected-volume') {
      return {
        verb: 'You injected',
        value: result.doseMg.toFixed(result.doseMg < 1 ? 3 : 2).replace(/\.?0+$/, ''),
        unit: 'mg',
      };
    }
    if (result.showsUnits) {
      const units = result.units;
      return {
        verb: 'Pull to',
        value: units.toFixed(units < 10 ? 1 : 0),
        unit: 'units',
      };
    } else {
      return {
        verb: 'Draw',
        value: result.volumeMl.toFixed(2),
        unit: 'mL',
      };
    }
  }

  function showOk(formatted, result) {
    resultEmpty.hidden = true;
    resultOverflow.hidden = true;

    if (result) {
      const hl = formatHeadline(result);
      if (headlineVerb) headlineVerb.textContent = hl.verb + ' ';
      if (headlineValue) headlineValue.textContent = hl.value;
      if (headlineUnit) headlineUnit.textContent = ' ' + hl.unit + '.';
      if (sublineEl) {
        const conc = result.concentrationMgPerMl.toFixed(2);
        const vol = result.volumeMl.toFixed(2);
        const base = '= ' + vol + ' mL of ' + conc + ' mg/mL solution';
        sublineEl.textContent = state.mode === 'injected-volume'
          ? base + ' (' + result.doseMcg.toFixed(result.doseMcg < 100 ? 1 : 0) + ' mcg)'
          : base;
      }
      const currentDoseMg = state.mode === 'injected-volume' ? result.doseMg : toDoseMg(state.dose, state.doseUnit);
      if (lifetimeEl && state.vial && currentDoseMg) {
        const doseMg = currentDoseMg;
        const dosesPerVial = doseMg > 0 ? Math.floor(state.vial / doseMg) : 0;
        if (isFinite(dosesPerVial) && dosesPerVial >= 1) {
          lifetimeEl.textContent = 'Vial gives you ~' + dosesPerVial + ' doses at this strength';
          lifetimeEl.hidden = false;
        } else {
          lifetimeEl.hidden = true;
        }
      }
      renderRoundTip(state.vial, currentDoseMg, SYRINGE_CAPACITY_ML[state.syringe]);
      renderSyringe(result);
      updateMiniFill(result.volumeMl, SYRINGE_CAPACITY_ML[state.syringe]);
      // Pre-fill Cycle Planner: link generated with current dose + vial in URL params
      if (planCycleLink && state.vial && currentDoseMg) {
        const params = new URLSearchParams({ start: currentDoseMg, vial: state.vial, freq: 1 });
        planCycleLink.href = '/pages/cycle-planner?' + params.toString();
      }
    }

    resultOk.hidden = false;
  }

  function showOverflow(formatted) {
    resultEmpty.hidden = true;
    resultOk.hidden = true;
    if (lifetimeEl) lifetimeEl.hidden = true;
    if (syringeBox) syringeBox.hidden = true;
    overflowMsg.textContent = formatted.message;
    resultOverflow.hidden = false;
  }

  function filterDosePillsByUnit(unit) {
    const step = root.querySelector('.is-calc__step[data-dose-step]');
    if (step) step.dataset.activeUnit = unit;
  }

  function clearDoseSelection() {
    state.dose = null;
    clearPillsInGroup('dose');
    const block = getOtherBlock('dose');
    if (block) {
      block.hidden = true;
      const input = block.querySelector('input');
      if (input) input.value = '';
      hideErr(block.querySelector('.is-calc__error'));
    }
  }

  function getDoseMgOrUnitKey() {
    return state.doseUnit === 'mg' ? 'dose_mg' : 'dose_mcg';
  }

  function filterInjectedPillsByUnit(unit) {
    const step = root.querySelector('.is-calc__injected-panel');
    if (step) step.dataset.activeInjectedUnit = unit;
  }

  function clearInjectedSelection() {
    state.injected = null;
    clearPillsInGroup('injected');
    const block = getOtherBlock('injected');
    if (block) {
      block.hidden = true;
      const input = block.querySelector('input');
      if (input) input.value = '';
      hideErr(block.querySelector('.is-calc__error'));
    }
  }

  function getInjectedLimitKey() {
    return state.injectedUnit === 'units' ? 'injected_units' : 'injected_ml';
  }

  function updateModeUI() {
    root.dataset.mode = state.mode;
    modeToggles.forEach(t => {
      const active = t.dataset.mode === state.mode;
      t.classList.toggle('is-calc__mode-toggle--active', active);
      t.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (step4Title) {
      step4Title.textContent = state.mode === 'injected-volume' ? 'Amount injected' : 'Dose';
    }
    if (dosePanel) dosePanel.hidden = state.mode !== 'target-dose';
    if (injectedPanel) injectedPanel.hidden = state.mode !== 'injected-volume';
    if (step4Hint) {
      step4Hint.textContent = state.mode === 'injected-volume'
        ? 'Enter what you pulled on the syringe. For U-100 syringes, 10 units equals 0.10 mL.'
        : 'Common starter doses: 250 mcg semaglutide, 2.5 mg tirzepatide. Always match what your prescriber wrote.';
    }
    if (resultEmpty) {
      resultEmpty.querySelector('p').textContent = state.mode === 'injected-volume'
        ? 'Choose a syringe, vial strength, water volume, and injected amount to see the dose.'
        : 'Choose a syringe, vial strength, water volume, and dose to see your draw.';
    }
  }

  function recompute() {
    if (restoreChip && !restoreChip.hidden) {
      const anySelected = state.syringe || state.vial || state.bac || state.dose || state.injected;
      if (anySelected) { restoreChip.hidden = true; restoreChip.style.display = 'none'; }
    }
    writeUrl();
    if (!state.syringe || !state.vial || !state.bac) {
      hideResult();
      return;
    }
    if (state.mode === 'target-dose' && !state.dose) {
      hideResult();
      return;
    }
    if (state.mode === 'injected-volume' && !state.injected) {
      hideResult();
      return;
    }
    const inputs = state.mode === 'injected-volume' ? {
      mode: state.mode,
      vialMg: state.vial,
      bacMl: state.bac,
      injectedValue: state.injected,
      injectedUnit: state.injectedUnit,
      syringeType: state.syringe,
    } : {
      mode: state.mode,
      vialMg: state.vial,
      bacMl: state.bac,
      doseValue: state.dose,
      doseUnit: state.doseUnit,
      syringeType: state.syringe,
    };
    const result = state.mode === 'injected-volume' ? calculateDoseFromInjectedVolume(inputs) : calculate(inputs);
    const formatted = formatOutput(result);
    if (formatted.type === 'overflow') {
      showOverflow(formatted);
    } else {
      showOk(formatted, result);
      saveLastCalc(inputs, window.localStorage, Date.now());
    }
    if (window.announceCalcResult) window.announceCalcResult(result.volumeMl);
  }

  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      const field = pill.dataset.field;
      const value = pill.dataset.value;

      clearPillsInGroup(field);
      selectPill(pill);

      const otherBlock = getOtherBlock(field);

      if (value === 'other') {
        if (otherBlock) {
          otherBlock.hidden = false;
          const input = otherBlock.querySelector('input');
          if (input) input.focus();
        }
        if (field === 'dose') state.dose = null;
        else state[field] = null;
        recompute();
        return;
      }

      if (otherBlock) {
        otherBlock.hidden = true;
        const input = otherBlock.querySelector('input');
        if (input) input.value = '';
        hideErr(otherBlock.querySelector('.is-calc__error'));
      }

      if (field === 'dose') {
        state.dose = parseFloat(value);
        const unit = pill.dataset.unit;
        if (unit && unit !== state.doseUnit) {
          state.doseUnit = unit;
          unitToggles.forEach(t => {
            const active = t.dataset.unit === unit;
            t.classList.toggle('is-calc__unit-toggle--active', active);
            t.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          filterDosePillsByUnit(unit);
        }
      } else if (field === 'syringe') {
        state.syringe = value;
        renderMiniSyringe(value);
      } else if (field === 'injected') {
        state.injected = parseFloat(value);
        const unit = pill.dataset.unit;
        if (unit && unit !== state.injectedUnit) {
          state.injectedUnit = unit;
          injectedUnitToggles.forEach(t => {
            const active = t.dataset.unit === unit;
            t.classList.toggle('is-calc__injected-unit-toggle--active', active);
            t.classList.toggle('is-calc__unit-toggle--active', active);
            t.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          filterInjectedPillsByUnit(unit);
        }
      } else {
        state[field] = parseFloat(value);
      }

      recompute();
    });
  });

  otherBlocks.forEach(block => {
    const field = block.dataset.field;
    const input = block.querySelector('input');
    const errEl = block.querySelector('.is-calc__error');
    if (!input) return;
    input.addEventListener('input', () => {
      const raw = input.value.trim();
      if (!raw) {
        if (field === 'dose') state.dose = null;
        else if (field === 'injected') state.injected = null;
        else state[field] = null;
        hideErr(errEl);
        recompute();
        return;
      }
      const v = parseFloat(raw);
      const limitKey = field === 'vial' ? 'vial_mg'
                     : field === 'bac'  ? 'bac_ml'
                     : field === 'injected' ? getInjectedLimitKey()
                     : getDoseMgOrUnitKey();
      const check = validateField(limitKey, v);
      if (!check.valid) {
        showErr(errEl, check.error);
        if (field === 'dose') state.dose = null;
        else if (field === 'injected') state.injected = null;
        else state[field] = null;
      } else {
        hideErr(errEl);
        if (field === 'dose') state.dose = v;
        else if (field === 'injected') state.injected = v;
        else state[field] = v;
      }
      recompute();
    });
  });

  unitToggles.forEach(toggle => {
    toggle.addEventListener('click', () => {
      const unit = toggle.dataset.unit;
      if (state.doseUnit === unit) return;
      state.doseUnit = unit;
      unitToggles.forEach(t => {
        const active = t.dataset.unit === unit;
        t.classList.toggle('is-calc__unit-toggle--active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      filterDosePillsByUnit(unit);
      clearDoseSelection();
      recompute();
    });
  });

  injectedUnitToggles.forEach(toggle => {
    toggle.addEventListener('click', () => {
      const unit = toggle.dataset.unit;
      if (state.injectedUnit === unit) return;
      state.injectedUnit = unit;
      injectedUnitToggles.forEach(t => {
        const active = t.dataset.unit === unit;
        t.classList.toggle('is-calc__injected-unit-toggle--active', active);
        t.classList.toggle('is-calc__unit-toggle--active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      filterInjectedPillsByUnit(unit);
      clearInjectedSelection();
      recompute();
    });
  });

  modeToggles.forEach(toggle => {
    toggle.addEventListener('click', () => {
      const mode = toggle.dataset.mode;
      if (state.mode === mode) return;
      state.mode = mode;
      updateModeUI();
      recompute();
    });
  });

  function resetAll() {
    state.mode = 'target-dose';
    state.syringe = null;
    state.vial = null;
    state.bac = null;
    state.dose = null;
    state.doseUnit = 'mcg';
    state.injected = null;
    state.injectedUnit = 'units';
    pills.forEach(p => {
      p.classList.remove('is-calc__pill--active');
      p.setAttribute('aria-checked', 'false');
    });
    otherBlocks.forEach(b => {
      b.hidden = true;
      const input = b.querySelector('input');
      if (input) input.value = '';
      hideErr(b.querySelector('.is-calc__error'));
    });
    unitToggles.forEach(t => {
      const active = t.dataset.unit === 'mcg';
      t.classList.toggle('is-calc__unit-toggle--active', active);
      t.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    filterDosePillsByUnit('mcg');
    injectedUnitToggles.forEach(t => {
      const active = t.dataset.unit === 'units';
      t.classList.toggle('is-calc__injected-unit-toggle--active', active);
      t.classList.toggle('is-calc__unit-toggle--active', active);
      t.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    filterInjectedPillsByUnit('units');
    updateModeUI();
    if (miniFluid) miniFluid.setAttribute('width', 0);
    if (miniSeal) miniSeal.setAttribute('x', 80);
    if (miniPlate) miniPlate.setAttribute('x', 80);
    if (miniRod) { miniRod.setAttribute('x', 80); miniRod.setAttribute('width', 0); }
    if (miniTicks) miniTicks.innerHTML = '';
    if (miniNums) miniNums.innerHTML = '';
    if (miniCaption) miniCaption.textContent = 'Pick a syringe to see it visualised';
    clearLastCalc(window.localStorage);
    hideResult();
    if (restoreChip) { restoreChip.hidden = true; restoreChip.style.display = 'none'; }
  }

  function formatRestoreSummary(data) {
    if (data.mode === 'injected-volume') {
      return `${data.vialMg}mg / ${data.bacMl}mL / ${data.injectedValue}${data.injectedUnit === 'units' ? ' units' : 'mL'}`;
    }
    return `${data.vialMg}mg / ${data.bacMl}mL / ${data.doseValue}${data.doseUnit}`;
  }

  function selectOrOther(field, value) {
    const presetPill = root.querySelector(`.is-calc__pill[data-field="${field}"][data-value="${value}"]`);
    clearPillsInGroup(field);
    if (presetPill && !presetPill.classList.contains('is-calc__pill--other')) {
      selectPill(presetPill);
      const block = getOtherBlock(field);
      if (block) block.hidden = true;
    } else {
      const otherPill = root.querySelector(`.is-calc__pill--other[data-field="${field}"]`);
      const block = getOtherBlock(field);
      if (otherPill) selectPill(otherPill);
      if (block) {
        block.hidden = false;
        const input = block.querySelector('input');
        if (input) input.value = value;
      }
      state[field] = parseFloat(value);
    }
  }

  function selectOrOtherDose(value, unit) {
    filterDosePillsByUnit(unit);
    clearPillsInGroup('dose');
    const presetPill = root.querySelector(`.is-calc__pill[data-field="dose"][data-value="${value}"][data-unit="${unit}"]`);
    if (presetPill) {
      selectPill(presetPill);
      const block = getOtherBlock('dose');
      if (block) block.hidden = true;
    } else {
      const otherPill = root.querySelector(`.is-calc__pill--other[data-field="dose"]`);
      const block = getOtherBlock('dose');
      if (otherPill) selectPill(otherPill);
      if (block) {
        block.hidden = false;
        const input = block.querySelector('input');
        if (input) input.value = value;
      }
      state.dose = parseFloat(value);
    }
  }

  function selectOrOtherInjected(value, unit) {
    filterInjectedPillsByUnit(unit);
    clearPillsInGroup('injected');
    const presetPill = root.querySelector(`.is-calc__pill[data-field="injected"][data-value="${value}"][data-unit="${unit}"]`);
    if (presetPill) {
      selectPill(presetPill);
      const block = getOtherBlock('injected');
      if (block) block.hidden = true;
    } else {
      const otherPill = root.querySelector(`.is-calc__pill--other[data-field="injected"]`);
      const block = getOtherBlock('injected');
      if (otherPill) selectPill(otherPill);
      if (block) {
        block.hidden = false;
        const input = block.querySelector('input');
        if (input) input.value = value;
      }
      state.injected = parseFloat(value);
    }
  }

  function applyRestoredData(data) {
    state.mode = data.mode || 'target-dose';
    state.syringe = data.syringeType;
    state.vial = data.vialMg;
    state.bac = data.bacMl;
    state.doseUnit = data.doseUnit || 'mcg';
    state.dose = data.doseValue || null;
    state.injectedUnit = data.injectedUnit || 'units';
    state.injected = data.injectedValue;
    updateModeUI();

    clearPillsInGroup('syringe');
    const syrPill = root.querySelector(`.is-calc__pill[data-field="syringe"][data-value="${data.syringeType}"]`);
    if (syrPill) selectPill(syrPill);
    renderMiniSyringe(data.syringeType);

    selectOrOther('vial', data.vialMg);
    selectOrOther('bac', data.bacMl);

    unitToggles.forEach(t => {
      const active = t.dataset.unit === state.doseUnit;
      t.classList.toggle('is-calc__unit-toggle--active', active);
      t.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (state.mode === 'target-dose') {
      selectOrOtherDose(data.doseValue, state.doseUnit);
    }
    injectedUnitToggles.forEach(t => {
      const active = t.dataset.unit === state.injectedUnit;
      t.classList.toggle('is-calc__injected-unit-toggle--active', active);
      t.classList.toggle('is-calc__unit-toggle--active', active);
      t.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (state.mode === 'injected-volume') {
      selectOrOtherInjected(data.injectedValue, state.injectedUnit);
    }

    recompute();
  }

  function maybeShowRestoreChip() {
    const data = loadLastCalc(window.localStorage, Date.now());
    if (!data) return;
    if (state.syringe || state.vial || state.bac || state.dose) return;
    if (!restoreChip) return;
    restoreSummary.textContent = formatRestoreSummary(data);
    restoreChip.hidden = false;
    restoreChip.style.display = '';
  }

  filterDosePillsByUnit('mcg');
  filterInjectedPillsByUnit('units');
  updateModeUI();

  if (resetBtn) resetBtn.addEventListener('click', resetAll);
  if (printBtn) printBtn.addEventListener('click', () => {
    const stamp = root.querySelector('#is-calc-result-printstamp');
    if (stamp) {
      const when = new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
      const activeAmount = state.mode === 'injected-volume'
        ? ((state.injected || '?') + (state.injectedUnit === 'units' ? ' units injected' : 'mL injected'))
        : ((state.dose || '?') + state.doseUnit + ' dose');
      const summary = state.mode + ' / ' + (state.vial || '?') + 'mg / ' + (state.bac || '?') + 'mL / ' + activeAmount
                    + ' / ' + (state.syringe || '?');
      stamp.textContent = 'injectionsupplies.com.au/pages/dose-volume-calculator | ' + when + ' | inputs: ' + summary;
    }
    window.print();
  });

  if (restoreYes) {
    restoreYes.addEventListener('click', () => {
      const data = loadLastCalc(window.localStorage, Date.now());
      if (data) applyRestoredData(data);
      if (restoreChip) { restoreChip.hidden = true; restoreChip.style.display = 'none'; }
    });
  }
  if (restoreNo) {
    restoreNo.addEventListener('click', () => {
      clearLastCalc(window.localStorage);
      if (restoreChip) { restoreChip.hidden = true; restoreChip.style.display = 'none'; }
    });
  }

  // Round-number tip (PeptideFox-style): suggest bac-water volumes that land the
  // draw on clean unit marks, for the current vial strength + dose + syringe.
  function renderRoundTip(vialMg, doseMg, capacityMl) {
    const el = document.getElementById('is-calc-round-tip');
    if (!el) return;
    if (!(vialMg > 0) || !(doseMg > 0) || !(capacityMl > 0)) { el.hidden = true; return; }
    const maxUnits = capacityMl * 100;
    const opts = [];
    [10, 20, 25, 50].forEach(function (u) {
      if (u > maxUnits + 1e-6) return;
      const bacMl = u * vialMg / (100 * doseMg);
      const rounded = Math.round(bacMl * 100) / 100;
      if (rounded >= 0.5 && rounded <= 5) opts.push({ units: u, bacMl: rounded });
    });
    if (!opts.length) { el.hidden = true; return; }
    opts.sort(function (a, b) {
      return Math.abs(a.bacMl - Math.round(a.bacMl * 2) / 2) - Math.abs(b.bacMl - Math.round(b.bacMl * 2) / 2);
    });
    const show = opts.slice(0, 2).map(function (o) { return o.bacMl + ' mL gives a round ' + o.units + '-unit draw'; });
    el.textContent = 'Round-number tip: ' + show.join('; ') + '.';
    el.hidden = false;
  }

  function writeUrl() {
    if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
    const params = new URLSearchParams();
    if (isFinite(state.vial) && state.vial > 0) params.set('vial', state.vial);
    if (state.mode !== 'injected-volume' && isFinite(state.dose) && state.dose > 0) {
      params.set('dose', state.dose);
      params.set('doseUnit', state.doseUnit);
    }
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  function applyUrlPrefill() {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('vial') && !params.has('dose')) return false;

    let touched = false;
    const vialRaw = params.get('vial');
    if (vialRaw !== null) {
      const v = parseFloat(vialRaw);
      const check = validateField('vial_mg', v);
      if (check.valid) {
        state.vial = v;
        selectOrOther('vial', v);
        touched = true;
      }
    }

    const doseRaw = params.get('dose');
    const unitRaw = params.get('doseUnit');
    if (doseRaw !== null) {
      const v = parseFloat(doseRaw);
      const unit = unitRaw === 'mg' || unitRaw === 'mcg' ? unitRaw : 'mg';
      const limitKey = unit === 'mg' ? 'dose_mg' : 'dose_mcg';
      const check = validateField(limitKey, v);
      if (check.valid) {
        state.doseUnit = unit;
        state.dose = v;
        unitToggles.forEach(t => {
          const active = t.dataset.unit === unit;
          t.classList.toggle('is-calc__unit-toggle--active', active);
          t.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        selectOrOtherDose(v, unit);
        touched = true;
      }
    }

    if (touched && restoreChip) {
      restoreChip.hidden = true;
      restoreChip.style.display = 'none';
    }
    return touched;
  }

  hideResult();
  const prefilled = applyUrlPrefill();
  if (prefilled) {
    recompute();
  } else {
    maybeShowRestoreChip();
  }
}
// cache bust 1777686897
